import {
  useGenericRefinement,
  BaseRefinementItem,
  RefinementConfig,
  RawRefinementItem,
} from '../../Algolia/useGenericRefinement';
import { FLESCH_READABILITY_LIST } from './FleschReadability';

// Direct mapping for Flesch Readability values
export const FLESCH_READABILITY_MAP: Record<string, string> = {
  'Very Easy to Read': 'Very Easy to Read',
  'Easy to Read': 'Easy to Read',
  'Fairly Easy to Read': 'Fairly Easy to Read',
  'Plain English': 'Plain English',
  'Fairly Difficult to Read': 'Fairly Difficult to Read',
  'Difficult to Read': 'Difficult to Read',
  'Very Difficult to Read': 'Very Difficult to Read',
};

export interface FleschReadabilityRefinementItem extends BaseRefinementItem {
  readabilityValue: string;
  readabilityName: string;
}

export interface FleschReadabilityMapping {
  readabilityMap: typeof FLESCH_READABILITY_MAP;
  readabilityList: typeof FLESCH_READABILITY_LIST;
}

export interface FleschReadabilityRefinementResult {
  items: FleschReadabilityRefinementItem[];
  refine: (value: string) => void;
  refineMultiple: (values: string[]) => void;
  clearRefinements: () => void;
  isSearchStalled: boolean;
  canRefine: boolean;
  getCountForValue: (value: string) => number;
  getTotalCount: () => number;
}

// Configuration for FleschReadability refinement
const fleschReadabilityConfig: RefinementConfig<
  FleschReadabilityRefinementItem,
  FleschReadabilityMapping
> = {
  attribute: 'metadata.lexile',
  mapping: {
    readabilityMap: FLESCH_READABILITY_MAP,
    readabilityList: FLESCH_READABILITY_LIST,
  },

  validateItem: (item: RawRefinementItem, mapping: FleschReadabilityMapping): boolean =>
    !!mapping.readabilityMap[item.value],

  transformItem: (
    item: RawRefinementItem,
    mapping: FleschReadabilityMapping,
  ): FleschReadabilityRefinementItem => ({
    label: item.value,
    value: item.value,
    count: item.count,
    isRefined: item.isRefined,
    readabilityValue: item.value,
    readabilityName: mapping.readabilityMap[item.value] || item.value,
  }),

  sortItems: (
    a: FleschReadabilityRefinementItem,
    b: FleschReadabilityRefinementItem,
  ): number => {
    const indexA = FLESCH_READABILITY_LIST.indexOf(a.readabilityValue);
    const indexB = FLESCH_READABILITY_LIST.indexOf(b.readabilityValue);
    return indexA - indexB;
  },

  getters: {
    getCountForValue: (
      items: FleschReadabilityRefinementItem[],
      value: string,
    ): number => {
      const item = items.find(
        (readabilityItem) => readabilityItem.readabilityValue === value,
      );
      return item?.count || 0;
    },
  },
};

/**
 * Custom hook for Flesch Readability refinements
 * Provides Algolia facet data specifically formatted for Flesch Readability values
 */
export const useFleschReadabilityRefinement = (
  attribute = 'metadata.lexile',
): FleschReadabilityRefinementResult => {
  const config = { ...fleschReadabilityConfig, attribute };
  const result =
    useGenericRefinement<FleschReadabilityRefinementItem, FleschReadabilityMapping>(
      config,
    );

  return {
    ...result,
    getCountForValue: result.getCountForValue,
  };
};
